import { EHttpMethods } from "../../../../enums";
import { getToken } from "../../../context/authContext";
import { ApiServiceManager, fetchRequest } from "../../apiServiceManager";
import { APP_PROPS_URL } from "./constants";
import {
  IAppPropBaseResponse,
  IAppPropListResponse,
  IAppPropResponse,
  IAppPropsByNamesResponse,
  ICreateAppPropBody,
  IUpdateAppPropBody,
} from "./models";

const defaultProvide = async () => await getToken();

const toNamesQuery = (names: string[]) =>
  names.map((name) => `names=${encodeURIComponent(name)}`).join("&");

/** Paged admin listing. Includes disabled props; soft-deleted props are never returned. */
export const getAppProps = (
  page = 0,
  size = 20,
  names: string[] = []
): Promise<IAppPropListResponse> => {
  const namesQuery = names.length ? `&${toNamesQuery(names)}` : "";
  const url = `${APP_PROPS_URL}?page=${page}&size=${size}&sort=id,desc${namesQuery}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/**
 * Runtime read: only enabled, non-deleted props. Unknown, disabled or deleted
 * names are left out of the result instead of failing the request.
 */
export const getEnabledAppPropsByNames = (
  names: string[]
): Promise<IAppPropsByNamesResponse> => {
  const url = `${APP_PROPS_URL}/by-names?${toNamesQuery(names)}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getAppProp = (id: number): Promise<IAppPropResponse> => {
  const url = `${APP_PROPS_URL}/${id}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const createAppProp = (
  body: ICreateAppPropBody
): Promise<IAppPropResponse> => {
  const descriptor = new ApiServiceManager(
    APP_PROPS_URL,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

/**
 * Replaces the whole value. List props have no per-item endpoint, so toggling
 * one org is a read-modify-write of the full string.
 */
export const updateAppPropValue = (
  id: number,
  body: IUpdateAppPropBody
): Promise<IAppPropResponse> => {
  const url = `${APP_PROPS_URL}/${id}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

/** Disabling makes runtime readers behave as if the prop did not exist. */
export const setAppPropEnabled = (
  id: number,
  enabled: boolean
): Promise<IAppPropResponse> => {
  const url = `${APP_PROPS_URL}/${id}/enabled`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, { enabled });
};

/** Soft-delete; the prop is also disabled. */
export const deleteAppProp = (id: number): Promise<IAppPropBaseResponse> => {
  const url = `${APP_PROPS_URL}/${id}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.DELETE,
    defaultProvide
  );
  return fetchRequest(descriptor);
};
